import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { profileHref } from "@/components/social/AuthorLink";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ConnectButton, MessageButton, FanFollowButtons } from "@/components/social/ProfileActions";
import { HelperNoteText } from "@/components/social/HelperNote";

// Otter Assist V1 Phase 6 + Phase 7 — the ONE reusable responder view (OAD-079 / OAD-088).
// Opened from the sender's own Otter Assist post ("N offered to help") AND from
// Engagement → Otter Assist → My Needs. One backend source, one presentation.
//
// The sender sees identities ONLY of members who intentionally offered to help — never a matched
// candidate, a queued candidate or a delivered recipient who did not respond. Withdrawn offers
// disappear from the count and the view while staying preserved internally. Per OAD-087 there is
// no members-reached, wave count or outreach aggregate anywhere in this view.
//
// Phase 7 adds the sender's PRIVATE tracking status. It is organisation only: it notifies nobody,
// never marks the need Help Received, never stops outreach and never affects matching or ranking.

const SOURCE_COPY = {
  assist_delivery: "Matched by Otter Assist",
  public_discovery: "Found your post",
};
const STATUS_COPY = {
  new: "New", contacted: "Contacted", helpful: "Helpful", not_pursued: "Not pursued",
};

// Exported so the Assistance Hub's Received view can reuse the SAME responder projection, the same
// private-status control and the same relationship actions instead of a second implementation.
export function ResponderRow({ assistId, responder }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState(responder.sender_status || "new");

  const save = useMutation({
    mutationFn: async (next) =>
      (await api.post(
        `/otter-assist/assists/${assistId}/responders/${responder.user_id}/status`,
        { status: next })).data,
    onSuccess: (_d, next) => {
      setStatus(next);
      toast.success(`Marked ${STATUS_COPY[next] || next} — only you can see this.`);
      qc.invalidateQueries({ queryKey: ["oa-responders", assistId] });
      // The same canonical row drives Assistance Received and My Needs — both must agree instantly.
      qc.invalidateQueries({ queryKey: ["oa-assistance-received"] });
      qc.invalidateQueries({ queryKey: ["oa-my-needs"] });
    },
    onError: () => toast.error("Something went wrong with Otter Assist. Please try again."),
  });

  return (
    <li className="py-3" data-testid={`otter-assist-responder-${responder.user_id}`}>
      <div className="flex items-start gap-3">
        {responder.headshot_url ? (
          <img src={responder.headshot_url} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
        ) : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-[13px] font-[640] text-muted-foreground">
            {(responder.display_name || "M").slice(0, 1)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {profileHref(responder) ? (
              <Link to={profileHref(responder)}
                className="truncate text-sm font-[640] text-foreground hover:underline"
                data-testid={`otter-assist-responder-profile-${responder.user_id}`}>
                {responder.display_name}
              </Link>
            ) : (
              <span className="truncate text-sm font-[640] text-foreground">{responder.display_name}</span>
            )}
            <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-[600] uppercase tracking-wide text-muted-foreground">
              {responder.profile_type || "personal"}
            </span>
          </div>
          {responder.headline && (
            <div className="truncate text-xs text-muted-foreground">{responder.headline}</div>
          )}
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {SOURCE_COPY[responder.source] || "Offered to help"}
            {responder.offered_at ? ` · ${new Date(responder.offered_at).toLocaleString()}` : ""}
          </div>
          {responder.why_you_matched && (
            <p className="mt-1.5 rounded-md border border-yellow-300/70 bg-yellow-50 px-2 py-1.5 text-[11px] text-foreground/90"
              data-testid={`otter-assist-responder-why-${responder.user_id}`}>
              {responder.why_you_matched}
            </p>
          )}

          {/* OAD-111 — the helper's own message. READ-ONLY here: only the helper can change it, and
              it is never rendered on the public Chatter post. */}
          <HelperNoteText note={responder.helper_note}
            withdrawn={responder.offer_state === "withdrawn"}
            testId={`otter-assist-responder-note-${responder.user_id}`} />

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <MessageButton targetUserId={responder.user_id} size="sm" variant="outline" />
            <ConnectButton targetUserId={responder.user_id} size="sm" />
            {responder.profile_id && (
              <FanFollowButtons targetType={responder.profile_type || "personal"}
                targetId={responder.profile_id} size="sm" showCount={false} />
            )}
          </div>

          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-[600] uppercase tracking-wide text-muted-foreground">
              Your private note
            </span>
            <Select value={status} onValueChange={(v) => save.mutate(v)}>
              <SelectTrigger className="h-7 w-[150px] text-[11px]"
                data-testid={`otter-assist-responder-status-${responder.user_id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_COPY).map(([value, label]) => (
                  <SelectItem key={value} value={value}
                    data-testid={`otter-assist-responder-status-${value}-${responder.user_id}`}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>
        </div>
      </div>
    </li>
  );
}

export function OtterAssistResponderModal({ assistId, open, onOpenChange }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["oa-responders", assistId],
    queryFn: async () => (await api.get(`/otter-assist/assists/${assistId}/responders`)).data,
    enabled: !!assistId && !!open,
  });
  const responders = data?.responders || [];

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto"
        data-testid="otter-assist-responder-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-teal-700" />
            {responders.length === 1 ? "1 person offered to help" : `${responders.length} offered to help`}
          </DialogTitle>
          <DialogDescription>
            These members chose to let you know they may be able to help. Your notes here are private.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : isError ? (
          <p className="py-6 text-center text-sm text-muted-foreground"
            data-testid="otter-assist-responders-error">
            Something went wrong with Otter Assist. Please try again.
          </p>
        ) : responders.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="otter-assist-responders-empty">
            No one has offered to help yet.
          </p>
        ) : (
          <ul className="divide-y divide-border" data-testid="otter-assist-responder-list">
            {responders.map((r) => (
              <ResponderRow key={r.user_id} assistId={assistId} responder={r} />
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default OtterAssistResponderModal;
